// apps-kb-main.js - Main keyboard navigation coordinator

import state from './apps-state.js';
import * as folder from './apps-folders.js';
import { getUserIconSizePreference, setUserIconSizePreference, calculateMaximumSliderPercentage, LAYOUT_CONFIG } from './apps-layout.js';
import * as render from './apps-render.js';
import { keyboardState } from './apps-kb-state.js';
import * as helpers from './apps-kb-helpers.js';
import * as cursor from './apps-kb-cursor.js';
import * as scroll from './apps-kb-scroll.js';
import * as modal from './apps-kb-modal.js';
import * as contextMenu from './apps-kb-context-menu.js';
import * as navigation from './apps-kb-navigation.js';
import * as moveMode from './apps-kb-move-mode.js';
import * as resizeHandler from './apps-resize.js';

async function handleKeyDown(e) {
    // Don't process keyboard navigation if edit modal is open
    const editModal = document.getElementById('editModal');
    if (editModal && editModal.classList.contains('open')) {
        return; // Let the modal handle its own keyboard input
    }
    
    if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.isContentEditable) {
        if (e.key === 'Escape' && e.target.id === 'folderTitle') {
            e.target.blur();
            return;
        }
        return;
    }
    
    if (keyboardState.contextMenu.active) {
        if (e.key === 'ArrowUp') {
            e.preventDefault();
            contextMenu.navigateContextMenu('up');
            return;
        } else if (e.key === 'ArrowDown') {
            e.preventDefault();
            contextMenu.navigateContextMenu('down');
            return;
        } else if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            contextMenu.activateContextMenuItem(() => moveMode.enterMoveMode());
            return;
        } else if (e.key === 'Escape') {
            e.preventDefault();
            contextMenu.hideContextMenu();
            return;
        } else if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
            e.preventDefault();
            contextMenu.hideContextMenu();
            const direction = e.key === 'ArrowLeft' ? 'left' : 'right';
            navigation.moveCursor(direction, moveMode.updateKeyboardDragIndicators);
            return;
        } else if (e.key === 'Tab') {
            e.preventDefault();
            contextMenu.hideContextMenu();
            if (keyboardState.virtualCursor.active) {
                cursor.hideVirtualCursor();
                if (e.shiftKey) {
                    focusIconSizer();
                } else {
                    const navItems = document.querySelectorAll('.nav-item');
                    if (navItems.length > 0) {
                        navItems[navItems.length - 1].focus();
                    }
                }
            }
            return;
        }
    }
    
    if (keyboardState.scrollZoneHover) {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            scroll.activateScrollZone(moveMode.updateKeyboardDragIndicators);
            return;
        } else if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
            e.preventDefault();
            scroll.hideScrollZoneHover();
            
            const layout = helpers.getLayout();
            const isFolder = !!state.openFolder;
            const icons = helpers.getCurrentIcons();
            
            if (keyboardState.moveMode.active) {
                moveMode.handleMoveModeHorizontalMovement(
                    e.key === 'ArrowLeft' ? 'left' : 'right',
                    layout,
                    isFolder,
                    icons
                );
            } else {
                navigation.moveCursor(e.key === 'ArrowLeft' ? 'left' : 'right', moveMode.updateKeyboardDragIndicators);
            }
            return;
        } else if (e.key === 'Escape') {
            e.preventDefault();
            scroll.hideScrollZoneHover();
            cursor.showVirtualCursor(keyboardState.virtualCursor.rank, keyboardState.virtualCursor.file);
            if (keyboardState.moveMode.active) {
                moveMode.updateKeyboardDragIndicators(keyboardState.virtualCursor.rank, keyboardState.virtualCursor.file);
            }
            return;
        }
    }
    
    if (keyboardState.virtualCursor.isInModalZone) {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            console.log('Space/Enter pressed in modal zone, calling handleModalZoneClick');
            modal.handleModalZoneClick();
            return;
        } else if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
            e.preventDefault();
            navigation.moveCursor(e.key === 'ArrowUp' ? 'up' : 'down', moveMode.updateKeyboardDragIndicators);
            return;
        } else if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
            e.preventDefault();
            modal.handleModalZoneMovement(e.key === 'ArrowLeft' ? 'left' : 'right');
            return;
        } else if (e.key === 'Escape') {
            e.preventDefault();
            // If in move mode, cancel the move and return icon to original position
            if (keyboardState.moveMode.active) {
                const position = keyboardState.virtualCursor.modalZonePosition;
                modal.unhighlightModalZone(position);
                moveMode.exitMoveMode(true);
            } else {
                // If not in move mode, just close the folder
                if (state.openFolder) {
                    folder.closeFolderView();
                }
            }
            return;
        }
    }
    
    if (keyboardState.moveMode.active) {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            moveMode.exitMoveMode(false);
            return;
        } else if (e.key === 'Escape') {
            e.preventDefault();
            moveMode.exitMoveMode(true);
            return;
        }
    }
    
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown' || e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        e.preventDefault();
        const direction = e.key.replace('Arrow', '').toLowerCase();
        
        const layout = helpers.getLayout();
        const isFolder = !!state.openFolder;
        const icons = helpers.getCurrentIcons();
        
        if (keyboardState.moveMode.active && (direction === 'left' || direction === 'right')) {
            moveMode.handleMoveModeHorizontalMovement(direction, layout, isFolder, icons);
        } else {
            navigation.moveCursor(direction, moveMode.updateKeyboardDragIndicators);
        }
        return;
    }
    
    if (e.key === 'Tab') {
        if (keyboardState.virtualCursor.active) {
            e.preventDefault();
            cursor.hideVirtualCursor();
            if (e.shiftKey) {
                // In folder, go to folder title
                if (state.openFolder) {
                    const folderTitle = document.getElementById('folderTitle');
                    if (folderTitle) {
                        folderTitle.focus();
                        return;
                    }
                }
                focusIconSizer();
            } else {
                const navItems = document.querySelectorAll('.nav-item');
                if (navItems.length > 0) {
                    navItems[navItems.length - 1].focus();
                }
            }
            return;
        }
    }
    
    // X key deletes empty screen if one exists on current screen (works even from scroll zones)
    if ((e.key === 'x' || e.key === 'X') && !keyboardState.moveMode.active) {
        e.preventDefault();
        if (helpers.isCurrentScreenEmptyWithDeleteButton()) {
            const deleteButton = helpers.getDeleteButtonElement();
            if (deleteButton) {
                const screenIndex = parseInt(deleteButton.dataset.screenIndex);
                const isFolder = deleteButton.dataset.isFolder === 'true';
                import('./apps-navigation.js').then(nav => {
                    nav.deleteEmptyScreen(screenIndex, isFolder);
                });
            }
        }
        return;
    }
    
    if (keyboardState.virtualCursor.active && !keyboardState.moveMode.active) {
        if (e.key === 'Enter') {
            e.preventDefault();
            // Check if on delete button
            if (keyboardState.virtualCursor.isOnDeleteButton) {
                const deleteButton = helpers.getDeleteButtonElement();
                if (deleteButton) {
                    const screenIndex = parseInt(deleteButton.dataset.screenIndex);
                    const isFolder = deleteButton.dataset.isFolder === 'true';
                    import('./apps-navigation.js').then(nav => {
                        nav.deleteEmptyScreen(screenIndex, isFolder);
                    });
                }
            } else if (keyboardState.contextMenu.active) {
                // Context menu is open - activate selected menu item
                contextMenu.activateContextMenuItem(() => moveMode.enterMoveMode());
            } else {
                // Context menu is not open - directly open/launch the icon
                contextMenu.performOpenAction();
            }
            return;
        }
        
        if (e.key === ' ') {
            e.preventDefault();
            // Check if on delete button
            if (keyboardState.virtualCursor.isOnDeleteButton) {
                const deleteButton = helpers.getDeleteButtonElement();
                if (deleteButton) {
                    const screenIndex = parseInt(deleteButton.dataset.screenIndex);
                    const isFolder = deleteButton.dataset.isFolder === 'true';
                    import('./apps-navigation.js').then(nav => {
                        nav.deleteEmptyScreen(screenIndex, isFolder);
                    });
                }
            } else {
                contextMenu.showContextMenu();
            }
            return;
        }
        
        if (e.key === 'm' || e.key === 'M') {
            e.preventDefault();
            // If context menu is open, close it first
            if (keyboardState.contextMenu.active) {
                contextMenu.hideContextMenu();
            }
            moveMode.enterMoveMode();
            return;
        }
        
        if (e.key === 'e' || e.key === 'E') {
            e.preventDefault();
            // If context menu is open, close it first
            if (keyboardState.contextMenu.active) {
                contextMenu.hideContextMenu();
            }
            contextMenu.performEditAction();
            return;
        }
    }
    
    if (e.key === 'Escape') {
        if (state.openFolder && keyboardState.virtualCursor.active && !keyboardState.moveMode.active) {
            e.preventDefault();
            const folderId = state.openFolder.id;
            folder.closeFolderView();
            navigation.handleFolderClose(folderId);
            return;
        }
        
        // Hide cursor on main screen if visible and not in special states
        if (!state.openFolder && keyboardState.virtualCursor.active && !keyboardState.moveMode.active && !keyboardState.contextMenu.active) {
            e.preventDefault();
            cursor.hideVirtualCursor();
            return;
        }
    }

    if ((e.key === '+' || e.key === '=') && !e.shiftKey) {
        e.preventDefault();
        const viewport = document.getElementById('appspaceViewport');
        const dynamicMax = calculateMaximumSliderPercentage(viewport.clientWidth, viewport.clientHeight);
        const current = getUserIconSizePreference();
        const newValue = Math.min(current + 0.125, dynamicMax);
        setUserIconSizePreference(newValue);
        
        // Update slider thumb position
        updateSliderThumbPosition(newValue, dynamicMax);
        
        await resizeHandler.recalculateLayoutWithCursorPreservation();
        return;
    }

    if (e.key === '-') {
        e.preventDefault();
        const viewport = document.getElementById('appspaceViewport');
        const dynamicMax = calculateMaximumSliderPercentage(viewport.clientWidth, viewport.clientHeight);
        const current = getUserIconSizePreference();
        const newValue = Math.max(current - 0.125, 0.5);
        setUserIconSizePreference(newValue);
        
        // Update slider thumb position
        updateSliderThumbPosition(newValue, dynamicMax);
        
        await resizeHandler.recalculateLayoutWithCursorPreservation();
        return;
    }
}

// Add this helper function
function updateSliderThumbPosition(value, dynamicMax) {
    const slider = document.getElementById('iconSizeSliderContainer');
    if (slider) {
        const thumb = slider.querySelector('[style*="border-radius: 50%"]');
        if (thumb) {
            const percent = (value - 0.5) / (dynamicMax - 0.5);
            thumb.style.left = `${percent * 100}%`;
        }
    }
}

function handleTabFromNav(shiftKey) {
    if (shiftKey) {
        focusIconSizer();
    } else {
        const layout = helpers.getLayout();
        const isFolder = !!state.openFolder;
        const icons = helpers.getCurrentIcons();
        
        if (isFolder && !keyboardState.virtualCursor.active) {
            const minRank = helpers.getMinOccupiedRank(icons, layout, isFolder);
            if (minRank >= 0) {
                const result = helpers.getLeftmostIconInRank(minRank, icons, layout, isFolder);
                if (result) {
                    cursor.showVirtualCursor(result.rank, result.file);
                }
            }
        } else if (!isFolder) {
            const minRank = helpers.getMinOccupiedRank(icons, layout, isFolder);
            if (minRank >= 0) {
                const result = helpers.getLeftmostIconInRank(minRank, icons, layout, isFolder);
                if (result) {
                    cursor.showVirtualCursor(result.rank, result.file);
                }
            }
        }
    }
}

function handleTabFromIconSizer() {
    const navItems = document.querySelectorAll('.nav-item');
    if (navItems.length > 0) {
        navItems[0].focus();
    }
}

function handleShiftTabFromIconSizer() {
    const layout = helpers.getLayout();
    const isFolder = !!state.openFolder;
    const icons = helpers.getCurrentIcons();
    
    const maxRank = helpers.getMaxOccupiedRank(icons, layout, isFolder);
    if (maxRank >= 0) {
        const result = helpers.getRightmostIconInRank(maxRank, icons, layout, isFolder);
        if (result) {
            cursor.showVirtualCursor(result.rank, result.file);
        }
    }
}

function focusIconSizer() {
    const slider = document.getElementById('iconSizeSlider');
    if (slider) {
        slider.focus();
        cursor.hideVirtualCursor();
    }
}

function setupNavTabHandling() {
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Tab' && !e.shiftKey) {
            const activeElement = document.activeElement;
            const navItems = Array.from(document.querySelectorAll('.nav-item'));
            const lastNavItem = navItems[navItems.length - 1];
            
            if (activeElement === lastNavItem) {
                e.preventDefault();
                handleTabFromNav(false);
            }
        }
    });
    
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Tab' && e.shiftKey) {
            const activeElement = document.activeElement;
            const navItems = Array.from(document.querySelectorAll('.nav-item'));
            const firstNavItem = navItems[0];
            
            if (activeElement === firstNavItem) {
                e.preventDefault();
                
                if (state.openFolder) {
                    const folderTitle = document.getElementById('folderTitle');
                    if (folderTitle) {
                        folderTitle.focus();
                        return;
                    }
                }
                
                handleTabFromNav(true);
            }
        }
    });
}

function setupIconSizerTabHandling() {
    const checkSlider = setInterval(() => {
        const slider = document.getElementById('iconSizeSlider');
        if (slider) {
            clearInterval(checkSlider);
            
            slider.addEventListener('keydown', (e) => {
                if (e.key === 'Tab') {
                    e.preventDefault();
                    if (e.shiftKey) {
                        handleShiftTabFromIconSizer();
                    } else {
                        handleTabFromIconSizer();
                    }
                }
            });
        }
    }, 100);
}

function handlePointerDragStart() {
    if (keyboardState.moveMode.active) {
        moveMode.exitMoveMode(true);
    }
    if (keyboardState.virtualCursor.active && state.isDraggingIcon) {
        cursor.hideVirtualCursor();
    }
}

/**
 * Set up focus event listeners on nav/header elements to hide virtual cursor
 */
function setupNavHeaderFocusHandling() {
    // Get all focusable elements in nav and header
    const nav = document.getElementById('navigation');
    const header = document.querySelector('.header');
    
    const focusableElements = [];
    
    if (nav) {
        // Find all focusable elements in nav
        const navFocusable = nav.querySelectorAll('a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])');
        focusableElements.push(...navFocusable);
    }
    
    if (header) {
        // Find all focusable elements in header
        const headerFocusable = header.querySelectorAll('a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])');
        focusableElements.push(...headerFocusable);
    }
    
    // Add focus listener to each element
    focusableElements.forEach(element => {
        element.addEventListener('focus', () => {
            if (keyboardState.virtualCursor.active) {
                cursor.hideVirtualCursor();
            }
        });
    });
}

export function initKeyboardNavigation() {
    document.addEventListener('keydown', handleKeyDown);
    
    const originalSetDraggingState = state.setDraggingState;
    state.setDraggingState = function(isDragging) {
        originalSetDraggingState.call(state, isDragging);
        
        if (isDragging && state.dragState && !state.dragState.isKeyboardDrag) {
            handlePointerDragStart();
        }
    };
    
    scroll.initScrollZoneStyles();
    setupNavTabHandling();
    setupIconSizerTabHandling();
    setupNavHeaderFocusHandling();
}

export function onFolderOpen() {
    // Only restore keyboard navigation if it was already active
    if (keyboardState.virtualCursor.active) {
        navigation.handleFolderOpen();
    }
}

export function onFolderClose(closedFolderId) {
    if (keyboardState.moveMode.active) return;
    // Only restore keyboard navigation if it was already active
    if (keyboardState.virtualCursor.active) {
        navigation.handleFolderClose(closedFolderId);
    }
}

export function getKeyboardState() {
    return keyboardState;
}
